import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';

import { logout } from '../../auth';
import { get, post } from '../../services/api';
import AdminSection from '../AdminSection/AdminSection';
import AdminTable from '../AdminTable/AdminTable';
import {
    buildLinhaSaqueFacil,
    formatMoeda,
    getColunasSaqueFacil,
    getLinhasPaginadas,
    getOpcoesPeriodo,
    getTotalPaginas,
    parseMoeda,
    LINHAS_POR_PAGINA
} from './ConsultaSaqueFacil.utils';

import './ConsultaSaqueFacil.css';

function getResponseErrorMessage(response, fallback) {
    return response?.response?.data?.Mensagem
        || response?.response?.data?.Error
        || response?.Mensagem
        || response?.message
        || fallback;
}

export default function ConsultaSaqueFacil({ setNotifier }) {
    const navigate = useNavigate();
    const optionsPeriodo = useMemo(() => getOpcoesPeriodo(), []);
    const selectStyle = useMemo(() => ({
        control: (base, state) => ({
            ...base,
            background: 'unset',
            borderColor: '#DCDCDC',
            borderRadius: '4px',
            boxShadow: state.isFocused ? '0 0 0 1px #2684FF' : 'none',
            minHeight: '38px'
        }),
        valueContainer: base => ({ ...base, padding: '0 8px' }),
        indicatorsContainer: base => ({ ...base, height: '38px' }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    }), []);

    const [authToken, setAuthToken] = useState(null);
    const [optionsFilial, setOptionsFilial] = useState([]);
    const [filialSelecionada, setFilialSelecionada] = useState(null);
    const [nomeVendedor, setNomeVendedor] = useState('');
    const [periodoSelecionado, setPeriodoSelecionado] = useState(
        optionsPeriodo.find(option => option.isCurrent)
    );
    const [vendedores, setVendedores] = useState(null);
    const [editandoVendedor, setEditandoVendedor] = useState(null);
    const [vendaEditada, setVendaEditada] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    async function buscarDados() {
        try {
            setIsLoading(true);
            const params = new URLSearchParams();
            params.append('idfiliais', filialSelecionada?.value || 0);
            params.append('mes', periodoSelecionado.mes);
            params.append('ano', periodoSelecionado.ano);

            if (nomeVendedor.trim()) {
                params.append('nome_vendedor', nomeVendedor.trim());
            }

            const response = await get(
                `/admin/saque-facil/vendedores?${params.toString()}`,
                null,
                authToken
            );

            if (!Array.isArray(response)) {
                setNotifier(false, 'Não foi possível consultar os vendedores.');
                setVendedores([]);
                return;
            }

            setVendedores(response);
            setEditandoVendedor(null);
            setVendaEditada(0);
            setPage(1);
        } catch (error) {
            setNotifier(false, 'Erro ao buscar vendedores. Tente novamente.');
            setVendedores([]);
        } finally {
            setIsLoading(false);
        }
    }

    function iniciarEdicao(vendedor) {
        setEditandoVendedor(vendedor.idvendedor);
        setVendaEditada(Number(vendedor.vendatotal) || 0);
    }

    function cancelarEdicao() {
        setEditandoVendedor(null);
        setVendaEditada(0);
    }

    async function salvarVenda(vendedor) {
        try {
            const response = await post('/admin/saque-facil/vendedores', {
                idvendedor: vendedor.idvendedor,
                mes: periodoSelecionado.mes,
                ano: periodoSelecionado.ano,
                vendatotal: vendaEditada,
                idusuario: localStorage.getItem('idusuario')
            }, authToken);

            if (!response?.Sucesso) {
                setNotifier(
                    false,
                    getResponseErrorMessage(response, 'Erro ao atualizar a venda.')
                );
                return;
            }

            setNotifier(true, response.Mensagem);
            await buscarDados();
        } catch (error) {
            setNotifier(false, 'Não foi possível atualizar a venda.');
        }
    }

    const dados = Array.isArray(vendedores)
        ? vendedores.map(vendedor => {
            const linha = buildLinhaSaqueFacil(vendedor);
            const isEditing = editandoVendedor === vendedor.idvendedor;

            return {
                ...linha,
                vendatotal: isEditing ? (
                    <input
                        type="text"
                        inputMode="numeric"
                        className="venda-input-inline"
                        value={formatMoeda(vendaEditada)}
                        onChange={event => setVendaEditada(parseMoeda(event.target.value))}
                        aria-label={`Venda total do vendedor ${vendedor.idvendedor}`}
                    />
                ) : linha.vendatotal,
                acao: isEditing ? (
                    <div className="action-buttons">
                        <button
                            type="button"
                            className="action-btn"
                            aria-label={`Confirmar venda do vendedor ${vendedor.idvendedor}`}
                            onClick={() => salvarVenda(vendedor)}
                        >
                            <CheckCircleIcon className="confirm-icon" />
                        </button>
                        <button
                            type="button"
                            className="action-btn"
                            aria-label={`Cancelar edição do vendedor ${vendedor.idvendedor}`}
                            onClick={cancelarEdicao}
                        >
                            <CancelIcon className="cancel-icon" />
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        className="action-btn edit-btn"
                        aria-label={`Editar venda do vendedor ${vendedor.idvendedor}`}
                        onClick={() => iniciarEdicao(vendedor)}
                    >
                        <EditIcon />
                    </button>
                )
            };
        })
        : null;

    const dadosPaginados = getLinhasPaginadas(dados, page);
    const numberOfPages = getTotalPaginas(dados);
    const tituloFiltros = editandoVendedor != null ? 'Consultar salário' : 'Selecione os filtros';

    useEffect(() => {
        const currentAuthToken = localStorage.getItem('adminToken');

        if (!currentAuthToken) {
            logout();
            navigate('/login');
            return;
        }

        setAuthToken(currentAuthToken);

        async function loadOptions() {
            const filiais = await get('/admin/metas/filial', null, currentAuthToken);

            const flatFiliais = [];
            if (Array.isArray(filiais)) {
                filiais.forEach(group => {
                    const items = group.items || group.options;
                    if (Array.isArray(items)) {
                        flatFiliais.push(...items);
                    } else if (group?.value !== undefined) {
                        flatFiliais.push(group);
                    }
                });
            }

            flatFiliais.sort((a, b) => Number(a.value) - Number(b.value));

            const filiaisFormatadas = flatFiliais.map(item => {
                const numeroFilial = Number(item.value) - 10000;
                const nomeFilial = String(item.label)
                    .replace(new RegExp(`\\s*-\\s*${numeroFilial}$`), '')
                    .trim();
                return { ...item, label: `${numeroFilial} - ${nomeFilial}` };
            });

            setOptionsFilial([{ label: 'Todas', value: 0 }, ...filiaisFormatadas]);
            setFilialSelecionada({ label: 'Todas', value: 0 });
        }

        loadOptions();
    }, [navigate]);

    return (
        <div className="saque-facil-page">
            <AdminSection
                etapa={1}
                titulo={tituloFiltros}
                contexto = "Use estes filtros para consultar e importar os dados de saque fácil dos vendedores."
                acoes={(
                    <button
                        type="button"
                        className="upload-btn"
                        onClick={() => navigate('/admin/saque-facil/importacao')}
                    >
                        Upload
                    </button>
                )}
            >
                <div className="consulta-saque-facil-filtros">
                    <div className="filtro-item">
                        <label htmlFor="filial">Filial</label>
                        <Select
                            inputId="filial"
                            className="react-select"
                            classNamePrefix="react-select"
                            options={optionsFilial}
                            value={filialSelecionada}
                            onChange={setFilialSelecionada}
                            placeholder="Todas"
                            menuPortalTarget={document.body}
                            styles={selectStyle}
                            noOptionsMessage={() => 'Nenhuma opção disponível'}
                        />
                    </div>

                    <div className="filtro-item">
                        <label htmlFor="nomeVendedor">Nome vendedor</label>
                        <input
                            id="nomeVendedor"
                            type="text"
                            placeholder="Todos"
                            value={nomeVendedor}
                            onChange={event => setNomeVendedor(event.target.value)}
                        />
                    </div>

                    <div className="filtro-item">
                        <label htmlFor="periodo">Período</label>
                        <Select
                            inputId="periodo"
                            className="react-select"
                            classNamePrefix="react-select"
                            options={optionsPeriodo}
                            value={periodoSelecionado}
                            onChange={setPeriodoSelecionado}
                            menuPortalTarget={document.body}
                            styles={selectStyle}
                            isSearchable={false}
                        />
                    </div>

                    <div className="filtro-item filtro-botao">
                        <button
                            type="button"
                            className="search-btn"
                            onClick={buscarDados}
                            disabled={isLoading || !authToken}
                        >
                            {isLoading ? 'Filtrando...' : 'Filtrar'}
                        </button>
                    </div>
                </div>
            </AdminSection>

            <AdminSection etapa={2} titulo="Acompanhamento de saques">
                <AdminTable
                    atributoKey="idvendedor"
                    colunas={getColunasSaqueFacil()}
                    dados={dadosPaginados}
                    styleName="tabela-saque-facil"
                    isPaginada={dados?.length > LINHAS_POR_PAGINA}
                    page={page}
                    setPage={setPage}
                    numberOfPages={numberOfPages}
                    setDadosByPage={setPage}
                />
            </AdminSection>
        </div>
    );
}
